import {z as zod} from "zod";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export const AMARON_ACTIONS_API_BASE_PATH = "/api/workspace-specific/amaron/actions";

export const amaronActionResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    callEventId: uuidZodValidator.nullable().optional(),
    enquiryEventId: uuidZodValidator.nullable().optional(),
    triggerEventId: uuidZodValidator.nullable().optional(),
    didScheduleViaTrigger: zod.boolean().optional(),
});
const amaronEventContainerNoteAttachmentResponseSchema = zod.object({
    assetId: uuidZodValidator,
    fileName: zod.string(),
    mimeType: zod.string().nullable().optional(),
    createdAt: zod.string(),
});
const amaronEventContainerNoteResponseSchema = zod.object({
    id: uuidZodValidator,
    eventContainerId: uuidZodValidator,
    bodyMarkdown: zod.string(),
    bodyPlainTextLength: zod.number(),
    createdBy: uuidZodValidator,
    createdAt: zod.string(),
    updatedAt: zod.string(),
    updatedBy: uuidZodValidator,
    attachments: zod.array(amaronEventContainerNoteAttachmentResponseSchema),
    scopeType: zod.enum(["event", "eventContainer"]),
    eventId: uuidZodValidator.nullable(),
    eventTemplateId: uuidZodValidator.nullable(),
});
const amaronEventContainerNoteMutationResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    note: amaronEventContainerNoteResponseSchema,
});
const amaronEventContainerNoteDeleteResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    noteId: uuidZodValidator,
});

export type AmaronActionResponse = zod.infer<typeof amaronActionResponseSchema>;
export type AmaronEventContainerNoteResponse = zod.infer<typeof amaronEventContainerNoteResponseSchema>;
export type AmaronEventContainerNoteMutationResponse = zod.infer<typeof amaronEventContainerNoteMutationResponseSchema>;
export type AmaronEventContainerNoteDeleteResponse = zod.infer<typeof amaronEventContainerNoteDeleteResponseSchema>;

export type AmaronLogCallRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid | null;
    disposition: Uuid;
    remarks: string;
    contactNumber: string;
    customerName: string;
    pincode?: string;
    customerCity: string;
    customerState: string;
    triggerEvent?: {
        scheduledOnIso: string;
    } | null;
};

export type AmaronScheduleCallRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid | null;
    contactNumber: string;
    customerName: string;
    pincode?: string;
    customerCity: string;
    customerState: string;
    scheduledOnIso: string;
};

export type AmaronScheduleCallInlineRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    scheduledOnIso: string;
};

export type AmaronDisposeRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    dispositionId: Uuid;
    remarks?: string | null;
    remarksColumnId?: Uuid;
    triggerEventAdditionalColumnValues?: Record<string, unknown>;
    contextEventAdditionalColumnValues?: Record<string, unknown>;
    eventContainerAdditionalColumnValues?: Record<string, unknown>;
    firstEnquiryEventId?: Uuid;
    firstEnquiryStatusRaw?: unknown;
};

export type AmaronEventContainerProfileUpdateRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    data?: {
        additionalColumnValues?: Record<string, unknown>;
    };
    eventUpdates?: Array<{
        eventId: Uuid;
        templateId: Uuid;
        data: {
            additionalColumnValues?: Record<string, unknown>;
        };
    }>;
};

export type AmaronEventContainerIdRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
};

export type AmaronCancelCallRequest = {
    workspaceId: Uuid;
    eventId: Uuid;
    cancellationReason: string;
};

export type AmaronUpdateEventRequest = {
    workspaceId: Uuid;
    eventId: Uuid;
    templateId: Uuid;
    eventPatch: {
        additionalColumnValues: Record<string, unknown>;
    };
    eventContainerPatch?: {
        additionalColumnValues: Record<string, unknown>;
    };
};

export type AmaronEventTemplateRequest = {
    workspaceId: Uuid;
    eventId: Uuid;
    templateId: Uuid;
};

export type AmaronEventContainerNoteAttachment = {
    assetId: Uuid;
    fileName: string;
    mimeType?: string | null;
    createdAt: string;
};

export type AmaronCreateEventContainerNoteRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    note: {
        bodyMarkdown: string;
        attachments: Array<AmaronEventContainerNoteAttachment>;
        scopeType?: "eventContainer";
    };
};

export type AmaronUpdateEventContainerNoteRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    noteId: Uuid;
    note: {
        bodyMarkdown: string;
        attachments: Array<AmaronEventContainerNoteAttachment>;
        scopeType?: "eventContainer";
    };
};

export type AmaronDeleteEventContainerNoteRequest = {
    workspaceId: Uuid;
    eventContainerId: Uuid;
    noteId: Uuid;
};

function buildAmaronActionHeaders(args: {
    accessToken: string;
    workspaceId: Uuid;
}): Record<string, string> {
    return {
        Authorization: `Bearer ${args.accessToken}`,
        "Content-Type": "application/json",
        "X-Workspace-Id": args.workspaceId,
    };
}

async function postAmaronAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    path: string;
    body: Record<string, unknown>;
}): Promise<Result<AmaronActionResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${AMARON_ACTIONS_API_BASE_PATH}${args.path}`,
        {
            method: "POST",
            headers: buildAmaronActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.workspaceId,
            }),
            body: JSON.stringify(args.body),
        },
        amaronActionResponseSchema,
    );
}

export function amaronLogCallAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronLogCallRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/log-call",
        body: args.request,
    });
}

export function amaronScheduleCallAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronScheduleCallRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/schedule-call",
        body: args.request,
    });
}

export function amaronScheduleCallInlineAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronScheduleCallInlineRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/schedule-call-inline",
        body: args.request,
    });
}

export function amaronDisposeAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronDisposeRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/dispose",
        body: args.request,
    });
}

export function amaronUpdateEventContainerProfileAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronEventContainerProfileUpdateRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/update-event-container-profile",
        body: args.request,
    });
}

export function amaronArchiveEventContainerAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronEventContainerIdRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/archive-event-container",
        body: args.request,
    });
}

export function amaronRestoreEventContainerAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronEventContainerIdRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/restore-event-container",
        body: args.request,
    });
}

export function amaronCancelCallAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronCancelCallRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/cancel-call",
        body: args.request,
    });
}

export function amaronUpdateEventAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronUpdateEventRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/update-event",
        body: args.request,
    });
}

export function amaronArchiveEventAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronEventTemplateRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/archive-event",
        body: args.request,
    });
}

export function amaronRestoreEventAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronEventTemplateRequest;
}): Promise<Result<AmaronActionResponse>> {
    return postAmaronAction({
        intellsysBaseUrl: args.intellsysBaseUrl,
        accessToken: args.accessToken,
        workspaceId: args.request.workspaceId,
        path: "/restore-event",
        body: args.request,
    });
}

export function amaronCreateEventContainerNoteAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronCreateEventContainerNoteRequest;
}): Promise<Result<AmaronEventContainerNoteMutationResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${AMARON_ACTIONS_API_BASE_PATH}/event-container-notes/create`,
        {
            method: "POST",
            headers: buildAmaronActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        amaronEventContainerNoteMutationResponseSchema,
    );
}

export function amaronUpdateEventContainerNoteAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronUpdateEventContainerNoteRequest;
}): Promise<Result<AmaronEventContainerNoteMutationResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${AMARON_ACTIONS_API_BASE_PATH}/event-container-notes/update`,
        {
            method: "POST",
            headers: buildAmaronActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        amaronEventContainerNoteMutationResponseSchema,
    );
}

export function amaronDeleteEventContainerNoteAction(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronDeleteEventContainerNoteRequest;
}): Promise<Result<AmaronEventContainerNoteDeleteResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${AMARON_ACTIONS_API_BASE_PATH}/event-container-notes/delete`,
        {
            method: "POST",
            headers: buildAmaronActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        amaronEventContainerNoteDeleteResponseSchema,
    );
}
